import React, { useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Heart, Calendar, Clock, CheckCircle, ArrowRight, AlertCircle } from 'lucide-react';
import { Button } from '../components/ui/button';

const WeddingTimelinePage = () => {
  const navigate = useNavigate();

  // Set page-specific meta tags
  useEffect(() => {
    document.title = 'Wedding Timeline Planning Guide | VowFlow';
    
    // Update meta description
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', 'Learn how to create a wedding timeline that adapts when plans change. Understand common timeline mistakes and how VowFlow helps you stay organized from engagement to "I do."');
    }
    
    // Update canonical
    const canonical = document.querySelector('link[rel="canonical"]');
    if (canonical) {
      canonical.setAttribute('href', 'https://vowflow.app/wedding-timeline');
    }
    
    // Cleanup on unmount
    return () => {
      document.title = 'VowFlow | Wedding Planning App – Timeline, Budget & Guest List Tools';
      if (metaDescription) {
        metaDescription.setAttribute('content', 'VowFlow is a wedding planning app that helps couples organize their timeline, budget, and guest list. Know what to do next with structured, stress-free planning.');
      }
      if (canonical) {
        canonical.setAttribute('href', 'https://vowflow.app/');
      }
    };
  }, []);

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#FAFAF9] to-[#F5F5F4]">
      {/* Navigation */}
      <nav className="px-6 md:px-12 py-6 flex justify-between items-center">
        <Link to="/" className="flex items-center gap-2">
          <Heart className="text-[#881337]" size={28} fill="#881337" />
          <span className="text-2xl font-serif font-bold tracking-tight text-[#1C1917]">VowFlow</span>
        </Link>
        <Button 
          onClick={() => navigate('/auth')} 
          className="rounded-full px-8 py-6 bg-[#881337] hover:bg-[#9F1239] text-white shadow-sm hover:shadow-md transition-all duration-300"
        >
          Get Started
        </Button>
      </nav>

      {/* Hero Section */}
      <section className="py-16 md:py-24 px-6 md:px-12 max-w-4xl mx-auto">
        <div className="text-center mb-12">
          <p className="text-xs uppercase tracking-widest text-[#A8A29E] mb-4">WEDDING PLANNING GUIDE</p>
          <h1 className="text-4xl md:text-5xl font-serif tracking-tight text-[#1C1917] mb-6">
            How to Create a Wedding Timeline That Actually Works
          </h1>
          <p className="text-lg text-[#57534E] max-w-2xl mx-auto">
            A wedding timeline is more than a schedule—it's the structure that keeps your planning on track. 
            Here's what most couples get wrong, and how to build a timeline that adapts when plans change.
          </p>
        </div>
      </section>

      {/* Main Content */}
      <article className="px-6 md:px-12 max-w-3xl mx-auto pb-24">
        
        {/* Section 1: What is a wedding timeline */}
        <section className="mb-16">
          <h2 className="text-2xl md:text-3xl font-serif text-[#1C1917] mb-6">
            What Is a Wedding Timeline?
          </h2>
          <p className="text-[#57534E] leading-relaxed mb-4">
            A wedding timeline is a structured plan that sequences every task from engagement to your wedding day. 
            It typically spans 12 to 18 months and includes milestones like booking vendors, sending invitations, 
            finalizing your guest list, and completing your day-of schedule.
          </p>
          <p className="text-[#57534E] leading-relaxed">
            The purpose of a wedding timeline is to reduce overwhelm. Instead of facing hundreds of decisions at once, 
            you address them in the right order—when they need to happen, not when anxiety tells you to.
          </p>
        </section>

        {/* Section 2: Common Mistakes */}
        <section className="mb-16">
          <h2 className="text-2xl md:text-3xl font-serif text-[#1C1917] mb-6">
            Common Wedding Timeline Mistakes
          </h2>
          
          <div className="space-y-6">
            <div className="flex gap-4">
              <AlertCircle className="text-[#881337] flex-shrink-0 mt-1" size={24} />
              <div>
                <h3 className="font-medium text-[#1C1917] mb-2">Starting with the wrong tasks</h3>
                <p className="text-[#57534E]">
                  Many couples rush to pick centerpieces or wedding favors before securing their venue or photographer. 
                  This creates stress when high-priority vendors become unavailable.
                </p>
              </div>
            </div>
            
            <div className="flex gap-4">
              <AlertCircle className="text-[#881337] flex-shrink-0 mt-1" size={24} />
              <div>
                <h3 className="font-medium text-[#1C1917] mb-2">Using a static checklist</h3>
                <p className="text-[#57534E]">
                  A generic checklist doesn't adapt when your wedding date changes, when vendors fall through, 
                  or when your budget shifts. Plans change—your timeline should too.
                </p>
              </div>
            </div>
            
            <div className="flex gap-4">
              <AlertCircle className="text-[#881337] flex-shrink-0 mt-1" size={24} />
              <div>
                <h3 className="font-medium text-[#1C1917] mb-2">Not connecting tasks to budget and guest count</h3>
                <p className="text-[#57534E]">
                  Your timeline, budget, and guest list are interconnected. Booking a venue for 200 guests 
                  when your list has 150 wastes money. Sending invitations before confirming your headcount leads to chaos.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Section 3: Why Timelines Need to Adapt */}
        <section className="mb-16">
          <h2 className="text-2xl md:text-3xl font-serif text-[#1C1917] mb-6">
            Why Your Timeline Needs to Adapt
          </h2>
          <p className="text-[#57534E] leading-relaxed mb-4">
            Wedding planning rarely goes exactly as expected. Venues get double-booked. Family circumstances change. 
            Budgets shift. A good timeline doesn't just tell you what to do—it adjusts when your situation changes.
          </p>
          <p className="text-[#57534E] leading-relaxed mb-4">
            When you move your wedding date, every milestone should shift accordingly. When you add 50 guests, 
            your catering timeline and budget should update. When you book a vendor, related tasks should mark themselves complete.
          </p>
          <p className="text-[#57534E] leading-relaxed">
            This is where most wedding planning tools fail. They give you a list, but not a system that responds to your real decisions.
          </p>
        </section>

        {/* Section 4: How VowFlow Helps */}
        <section className="mb-16">
          <h2 className="text-2xl md:text-3xl font-serif text-[#1C1917] mb-6">
            How VowFlow Helps You Plan with Confidence
          </h2>
          
          <div className="bg-white rounded-2xl border border-stone-200 p-8 space-y-6">
            <div className="flex gap-4">
              <div className="w-10 h-10 rounded-full bg-rose-50 flex items-center justify-center flex-shrink-0">
                <Calendar className="text-[#881337]" size={20} />
              </div>
              <div>
                <h3 className="font-medium text-[#1C1917] mb-2">Automatic timeline generation</h3>
                <p className="text-[#57534E]">
                  Enter your wedding date, and VowFlow creates a personalized timeline with tasks sequenced correctly. 
                  No guessing what comes next.
                </p>
              </div>
            </div>
            
            <div className="flex gap-4">
              <div className="w-10 h-10 rounded-full bg-rose-50 flex items-center justify-center flex-shrink-0">
                <Clock className="text-[#881337]" size={20} />
              </div>
              <div>
                <h3 className="font-medium text-[#1C1917] mb-2">Tasks that adapt to changes</h3>
                <p className="text-[#57534E]">
                  When your wedding date moves, your timeline adjusts. When you complete a task in one area, 
                  related tasks update automatically.
                </p>
              </div>
            </div>
            
            <div className="flex gap-4">
              <div className="w-10 h-10 rounded-full bg-rose-50 flex items-center justify-center flex-shrink-0">
                <CheckCircle className="text-[#881337]" size={20} />
              </div>
              <div>
                <h3 className="font-medium text-[#1C1917] mb-2">Know what matters next</h3>
                <p className="text-[#57534E]">
                  VowFlow shows you exactly what to focus on right now—not everything at once. 
                  This reduces decision fatigue and keeps you moving forward.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="text-center bg-gradient-to-br from-[#881337]/5 to-[#881337]/10 rounded-2xl p-12">
          <h2 className="text-2xl md:text-3xl font-serif text-[#1C1917] mb-4">
            Ready to Plan Without the Overwhelm?
          </h2>
          <p className="text-[#57534E] mb-8 max-w-lg mx-auto">
            VowFlow helps you build a wedding timeline that works—one that adapts to your decisions 
            and keeps you focused on what matters next.
          </p>
          <Button 
            onClick={() => navigate('/auth')} 
            className="rounded-full px-8 py-6 bg-[#881337] hover:bg-[#9F1239] text-white text-base shadow-md hover:shadow-xl transition-all duration-300"
          >
            Start Planning <ArrowRight className="ml-2" size={20} />
          </Button>
          <p className="text-sm text-[#A8A29E] mt-4">
            <Link to="/" className="hover:text-[#881337] transition-colors">← Back to VowFlow Home</Link>
          </p>
        </section>

      </article>

      {/* Footer */}
      <footer className="py-12 px-6 md:px-12 border-t border-stone-200">
        <div className="max-w-7xl mx-auto flex flex-col md:flex-row justify-between items-center gap-6">
          <div className="flex items-center gap-2">
            <Heart className="text-[#881337]" size={20} fill="#881337" />
            <span className="text-lg font-serif font-bold text-[#1C1917]">VowFlow</span>
          </div>
          <p className="text-sm text-[#A8A29E]">© 2025 VowFlow. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
};

export default WeddingTimelinePage;
